import React, { memo, useRef } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import type { SectionWrapperPropagationProps } from "../../../common/section-wrapper/SectionWrapper";
import SectionWrapper from "../../../common/section-wrapper/SectionWrapper";
import { CarConfigConfigureSectionsLbx } from "../../../../../utils/types/SectionTypesLbx";
import Container from "../../../../../../shared-components/css-modules/lexus/atoms/Container";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import slider from "../../../../../../shared-components/css-modules/lexus/utils/DraggableSlider.module.scss";
import styles from "./Powertrain.module.scss";
import Wheeldrive from "./Wheeldrive";
import { useSlider } from "../../../../../../../../common-deprecated/features/apheleia/hooks/useSlider";
import { useCarConfigSelector } from "../../../../../redux/store";
import { selectCurrentAtmosphere } from "../../../../../redux/slices/CarConfigAtmosphereSlice";
import { CarConfigLabel } from "../../../../../utils/labelUtils";

const Powertrain: React.FC<SectionWrapperPropagationProps<CarConfigConfigureSectionsLbx>> = ({
    sectionRefs,
}): JSX.Element | null => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const anyItemRef = useRef<HTMLDivElement | null>(null);

    // Get all the wheeldrives in current grade.
    const wheelDrivesInCurrentGrade = useCarConfigSelector(selectCurrentAtmosphere, shallowEqual)?.wheeldrives;
    // LBX should only have one engine available
    const currentEngineDescription = useCarConfigSelector(
        (state) => state.carConfigMotorization.motorization[0].engine.name,
    );

    const { elementProps, isDragging } = useSlider(wrapperRef, anyItemRef);

    // TODO The has badges variant had to be there when not every tier is unlocked based on grade selection. This allows room for the "unlock" badge in the slider
    const hasBadges = true;

    return (
        <SectionWrapper
            sectionId={CarConfigConfigureSectionsLbx.Powertrain}
            sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.Powertrain)}
        >
            <Container>
                <header className={styles.header}>
                    <Text tag="h2" variant="apheleia-h3" spacing="spacing-2">
                        <CarConfigLabel label="carConfigLbxPowertrainTitle" />
                    </Text>
                    <Text variant="apheleia-body" spacing="spacing-5">
                        <CarConfigLabel
                            label="carConfigLbxPowertrainDescription"
                            params={{ engine: currentEngineDescription }}
                        />
                    </Text>
                </header>
            </Container>
            <div
                className={clsx(
                    slider.wrapper,
                    styles.slider,
                    isDragging && slider.dragging,
                    hasBadges && styles["has-badges"],
                )}
                ref={wrapperRef}
                {...elementProps}
            >
                {wheelDrivesInCurrentGrade?.map((wheeldrive) => (
                    <Wheeldrive key={wheeldrive.id} wheeldrive={wheeldrive} ref={anyItemRef} />
                ))}
            </div>
        </SectionWrapper>
    );
};

export default memo(Powertrain);
